import { call, put, takeLatest, select } from "redux-saga/effects";

import { types, actions, selectors } from "reducers/token";

import * as tokenMidleware from "middleware/token";

import { actions as notificationActions } from "reducers/notification";
import { selectors as sessionSelectors } from "reducers/session";
import getLoggedUserId from "util/session";

import * as i18n from "util/i18n";
import { push } from "react-router-redux";
import isTokenActive from "util/token";
import { adjustIdFieldErrors } from "util/form.js";

import * as deviceUtils from "util/device";
import * as configUtils from "util/config";
import * as secureStorageUtils from "util/secureStorage";
import * as tokenUtils from "util/token";
import * as fingerprintUtils from "util/fingerprint";
import { waitForNativeBootstrapReady } from "util/nativeDeviceReady";

const TOKEN_SEED = "tokenSeed";

const sagas = [
    takeLatest(types.TOKEN_CREATE_PREVIEW_REQUEST, tokenCreatePreview),
    takeLatest(types.TOKEN_CREATE_REQUEST, tokenCreate),

    takeLatest(types.TOKEN_ACTIVATION_PRE_REQUEST, tokenActivationPre),
    takeLatest(types.TOKEN_ACTIVATION_PREVIEW_REQUEST, tokenActivationPreview),
    takeLatest(types.TOKEN_ACTIVATION_REQUEST, tokenActivation),

    takeLatest(types.TOKEN_BIOMETRIC_ACTIVATION_REQUEST, tokenBiometricActivation),

    takeLatest(types.TOKEN_BIOMETRIC_VERIFY_REQUEST, tokenBiometricVerify),

    takeLatest(types.CHECK_USER_ACTIVE_TOKEN, checkUserActiveTokenWeb),
    takeLatest(types.TOKEN_SCREEN_NATIVE_BOOTSTRAP, tokenScreenNativeBootstrap),

    takeLatest(types.TOKEN_CHANGE_PASSWORD_REQUEST, changeTokenPass),

    takeLatest(types.TOKEN_RECOVER_PASSWORD_REQUEST, recoverTokenPass),

    takeLatest(types.TOKEN_PASSWORD_VALIDATION_REQUEST, tokenPasswordValidation),
    takeLatest(types.TOKEN_PASSWORD_GENERATION_REQUEST, tokenGeneration),

    takeLatest(types.SOFT_TOKEN_GENERATION_REQUEST, softTokenGeneration),
    takeLatest(types.SOFT_TOKEN_SEED_CREATE, createTokenSeed),
    takeLatest(types.SOFT_TOKEN_MIGRATE_SEED_REQUEST, migrateTokenSeed),

    takeLatest(types.EXCHANGE_TOKEN_REFRESH_REQUEST, refreshExchangeToken),
    takeLatest(types.EXCHANGE_TOKEN_REFRESH_CHECK, checkExchangeToken),

    takeLatest(types.TOKEN_PASSWORD_DISABLED_REQUEST, tokenPasswordDisabled),

    takeLatest(types.TOKEN_BIOMETRIC_DISABLED_REQUEST, tokenBiometricDisabled),

    takeLatest(types.TOKEN_STATUS_PASSWORD_REQUEST, getTokenStatusMobile),
    takeLatest(types.TOKEN_STATUS_PASSWORD_ANONYMOUS_REQUEST, getTokenStatusMobileAnonymous),

    takeLatest(types.TOKEN_OTHER_DEVICES_NEW_TOKEN, changeNewToken),

    takeLatest(types.TOKEN_SYNC_NEW_DEVICE_PREVIEW_REQUEST, tokenOTPValidation),

    takeLatest(types.TOKEN_SYNC_NEW_DEVICE_REQUEST, tokenSyncNewDevice),

    takeLatest(types.TOKEN_SEND_NEW_CODE_REQUEST, tokenActivationSendNewCode),

    takeLatest(types.TOKEN_QUERY_CREDENTIAL, tokenQueryCredential),

    takeLatest(types.TOKEN_GENERATED_VALUE, getGeneratedTokenValue),
    takeLatest(types.TOKEN_GENERATED_VALUE_NUMBER, generatedTokenNumber),
    takeLatest(types.TOKEN_RECOVER_ANONYMOUS_REQUEST, recoverTokenAnonymous),
    takeLatest(types.TOKEN_STATUS_PASSWORD_REVOKE, deleteDeviceWithoutSeed),
    takeLatest(types.SOFT_TOKEN_SEED_REMOVE, deleteSeedFromDevice),

    takeLatest(types.TOKEN_SEED_VERIFY, verifyTokenSeed),
    takeLatest(types.TOKEN_BIOMETRIC_ACCESS_REQUEST, validateBiometrics),
    takeLatest(types.TOKEN_BIOMETRIC_DISABLE_PRE_REQUEST, tokenBiometricDisablePre),
    takeLatest(types.TOKEN_BIOMETRICS_GENERATOR, generateBiometricsToken),
    takeLatest(types.SESSION_EXPIRED_REQUEST, sessionExpired),
];

export default sagas;

function* tokenCreatePreview(params) {
    const { idForms, userId } = params;

    let idDevice = null;

    if (window.app) {
        idDevice = window.app.getDeviceUUID();
    } else {
        idDevice = yield deviceUtils.getDeviceFingerprint();
    }
    if (idDevice) {
        const response = yield call(tokenMidleware.tokenCreatePreview, idDevice, idForms, userId);
        if (response.type === "W") {
            yield put(actions.tokenCreatePreviewFailed());
            if (response.data.data.NO_FIELD) {
                yield put(
                    notificationActions.showNotification(response.data.data.NO_FIELD, "error", [
                        "tokenCreateCodeInvitation",
                    ]),
                );
            } else {
                yield put(
                    notificationActions.showNotification(i18n.get("global.unexpectedError"), "error", [
                        "tokenCreateCodeInvitation",
                    ]),
                );
            }
        } else {
            yield put(actions.tokenCreatePreviewSuccess(response.data.data));
        }
    }
}

function* tokenCreate(params) {
    const response = yield call(tokenMidleware.tokenCreate, params.sendChannel);
    if (response.type === "W") {
        if (response.data.code === "COR020W") {
            yield put(actions.tokenCreateFailed());

            yield put(
                notificationActions.showNotification(response.data.data.NO_FIELD, "error", [
                    "tokenCreateCodeInvitation",
                ]),
            );
        } else {
            yield put(actions.tokenCreateFailed());
            yield put(
                notificationActions.showNotification(response.data.message, "error", ["tokenCreateCodeInvitation"]),
            );
        }
    } else {
        const showTokenForm = configUtils.getBoolean("frontend.show.formularioSolicitarToken.functionalities", false);
        const successText = showTokenForm ? "setting.token.request.noForm.success" : "setting.token.request.success";
        yield put(
            notificationActions.showNotification(i18n.get(successText), "success", ["tokenCreateCodeInvitation"]),
        );
        yield put(actions.tokenCreateSuccess(response.data.data));

        const router = yield select((state) => state.router);
        const pathname = router && router.location && router.location.pathname;
        if (pathname === "/settings/token/request") {
            yield put(push("/settings/token"));
        }

        yield put(actions.checkUserActiveToken());
    }
}

function* tokenQueryCredential(params) {
    if (isTokenActive()) {
        yield call(getGeneratedTokenValue, params);
    }
}

function* getGeneratedTokenValue(params) {
    const { form, field } = params;

    const idDevice = window.app.getDeviceUUID();

    const useOfflineToken = configUtils.getBoolean("backend.show.Token.offline");
    if (useOfflineToken) {
        try {
            const response = yield call(getOfflineTokenValue);
            if (!response) {
                yield put(notificationActions.showNotification("Error interno", "error", ["tokenPassValidation"]));
            } else if (form && field) {
                const { token } = response;
                form.setValues({ ...form.values, [field.name]: token });
            }
        } catch (e) {
            yield call(handleSeedError);
        }
    } else {
        const response = yield call(tokenMidleware.tokenGeneration, idDevice);

        if (response.type === "W") {
            yield put(notificationActions.showNotification("Error interno", "error", ["tokenPassValidation"]));
        } else if (form && field) {
            const { _tokenNumber } = response.data.data;
            form.setValues({ ...form.values, [field.name]: _tokenNumber });
        }
    }
}

function* generatedTokenNumber() {
    const idDevice = window.app.getDeviceUUID();

    const useOfflineToken = configUtils.getBoolean("backend.show.Token.offline");
    if (useOfflineToken) {
        const response = yield call(getOfflineTokenValue);
        if (!response) {
            yield put(notificationActions.showNotification("Error interno", "error", ["tokenPassValidation"]));
        } else {
            const { token } = response;
            yield put(actions.tokenPasswordGenerationSuccess({ _tokenNumber: token }));
        }
    }

    const response = yield call(tokenMidleware.tokenGeneration, idDevice);

    if (response.type === "W") {
        yield put(notificationActions.showNotification("Error interno", "error", ["tokenPassValidation"]));
    } else {
        yield put(actions.tokenPasswordGenerationSuccess(response.data.data));
    }
}

function* tokenActivationPre() {
    const response = yield call(tokenMidleware.tokenActivationPre);

    if (response.type === "W") {
        yield put(actions.tokenActivationPreFailed());

        if (response.data.data.NO_FIELD) {
            yield put(
                notificationActions.showNotification(response.data.data.NO_FIELD, "error", [
                    "tokenCreateCodeInvitation",
                ]),
            );
        } else {
            yield put(
                notificationActions.showNotification(i18n.get("global.unexpectedError"), "error", [
                    "tokenCreateCodeInvitation",
                ]),
            );
        }
    } else {
        if (!response.data.data.isQuery) {
            yield put(actions.tokenActivationPreFailed());
            yield put(
                notificationActions.showNotification(
                    i18n.get("setting.token.mobile.activation.request.failure"),
                    "warning",
                    ["tokenCreateCodeInvitation"],
                ),
            );
        } else {
            yield put(push("/settings/token/codeActivation"));
        }
        yield put(actions.tokenActivationPreSuccess(response.data.data));
    }
}

function* tokenActivationPreview(params) {
    const code = params.codeInvite;
    let response = yield call(tokenMidleware.tokenActivationPreview, code);

    if (response.type === "W") {
        yield put(actions.tokenActivationPreviewFailed());

        if (response.data.data.NO_FIELD) {
            if (response.data.data.NO_FIELD === "setting.token.activation.newCode.limitError") {
                response = yield call(tokenMidleware.tokenActivationSendNewCode);
                if (response.type === "W") {
                    yield put(
                        notificationActions.showNotification(
                            i18n.get("setting.token.activation.newCode.failed"),
                            "error",
                            ["tokenCreateCodeInvitation"],
                        ),
                    );
                } else {
                    yield put(
                        notificationActions.showNotification(response.data.data.NO_FIELD, "error", [
                            "tokenCreateCodeInvitation",
                        ]),
                    );
                }
            } else {
                yield put(
                    notificationActions.showNotification(response.data.data.NO_FIELD, "error", [
                        "tokenCreateCodeInvitation",
                    ]),
                );
            }
        } else {
            yield put(
                notificationActions.showNotification(i18n.get("global.unexpectedError"), "error", [
                    "tokenCreateCodeInvitation",
                ]),
            );
        }
    } else {
        yield put(actions.tokenActivationPreviewSuccess({ ...response.data.data, code }));

        const useBiometricToken = configUtils.getBoolean("backend.show.Biometric.Token.offline");
        if (useBiometricToken) {
            yield put(push("/settings/token/codeActivation/biometricActivation"));
        } else {
            yield put(push("/settings/token/codeActivation/changuedPassword"));
        }
    }
}

function* tokenActivationSendNewCode() {
    const response = yield call(tokenMidleware.tokenActivationResendCode);

    if (response.type === "W") {
        const message = response.data?.data?.NO_FIELD;

        yield put(actions.tokenActivationSendNewCodeFailed());
        yield put(
            notificationActions.showNotification(
                message || i18n.get("setting.token.activation.newCode.failed"),
                "error",
                ["tokenCreateCodeInvitation"],
            ),
        );
    } else {
        yield put(actions.tokenActivationSendNewCodeSuccess(response.data.data));
        yield put(
            notificationActions.showNotification(i18n.get("setting.token.activation.newCode.success"), "success", [
                "tokenCreateCodeInvitation",
            ]),
        );
    }
}

function* checkUserActiveTokenWeb() {
    const response = yield call(tokenMidleware.getTokenStatus);
    if (response.type === "W" || !response.data || !response.data.data) {
        yield put(actions.checkUserActiveTokenFailed());
        return;
    }
    const { statusToken } = response.data.data;
    yield put({
        type: types.CHECK_USER_ACTIVE_TOKEN_SUCCESS,
        webTokenStatus: statusToken,
    });
}

/** Primera carga de Token en app nativa: espera bridge/UUID y luego estado web + dispositivo en serie. */
function* tokenScreenNativeBootstrap(action) {
    const { userId } = action;
    const ready = yield call(waitForNativeBootstrapReady, 10000);
    if (!ready) {
        yield put(actions.checkUserActiveTokenFailed());
        yield put(actions.tokenStatusPasswordFailed());
        return;
    }
    yield call(checkUserActiveTokenWeb);
    yield call(getTokenStatusMobile, { userId });
}

function* tokenActivation(params) {
    const { password, passwordConfirmation, formikBag, code } = params;
    const idDevice = window.app.getDeviceUUID();
    const response = yield call(tokenMidleware.tokenActivation, password, passwordConfirmation, idDevice, code);
    if (response.type === "W") {
        if (response.data.data.NO_FIELD) {
            yield put(
                notificationActions.showNotification(response.data.data.NO_FIELD, "error", [
                    "tokenCreateCodeInvitation",
                ]),
            );
        } else if (response.data.code === "API527W") {
            formikBag.setErrors({ password: response.data.message });
        } else {
            yield put(
                notificationActions.showNotification(i18n.get("global.unexpectedError"), "error", [
                    "tokenCreateCodeInvitation",
                ]),
            );
        }
        yield put(actions.tokenActivationFailed());
    } else {
        yield put(push("/settings/token"));
        if (window.isMobileApp()) {
            const userId = getLoggedUserId();
            yield put({
                type: types.TOKEN_STATUS_PASSWORD_REQUEST,
                userId,
            });
        }
        const useOfflineToken = configUtils.getBoolean("backend.show.Token.offline");
        if (useOfflineToken) {
            yield put(actions.softTokenSeedcreate(response.data.data._seed));
        }
        yield put(actions.tokenActivationSuccess());
    }
}

function* tokenBiometricActivation(params) {
    const { code } = params;
    const idDevice = window.app.getDeviceUUID();
    const response = yield call(tokenMidleware.tokenBiometricActivation, idDevice, code);
    if (response.type === "W") {
        if (response.data.data.NO_FIELD) {
            yield put(
                notificationActions.showNotification(response.data.data.NO_FIELD, "error", [
                    "tokenCreateCodeInvitation",
                ]),
            );
        } else if (response.data.code === "API527W") {
            // ver alternativa
            // formikBag.setErrors({ password: response.data.message });
        } else {
            yield put(
                notificationActions.showNotification(i18n.get("global.unexpectedError"), "error", [
                    "tokenCreateCodeInvitation",
                ]),
            );
        }
        yield put(actions.tokenActivationFailed());
    } else {
        yield put(push("/settings/token"));
        if (window.isMobileApp()) {
            const userId = getLoggedUserId();
            yield put({
                type: types.TOKEN_STATUS_PASSWORD_REQUEST,
                userId,
            });
        }
        yield put(actions.softTokenSeedcreate(response.data.data._seed));
        yield put(actions.tokenActivationSuccess());
    }
}

function* changeTokenPass({ password, newPassword, newPasswordConfirmation, formikBag }) {
    const response = yield call(tokenMidleware.changeTokenPass, password, newPassword, newPasswordConfirmation);
    if (response.type === "W") {
        const errorMessage =
            response.data?.data?.NO_FIELD || response.data.message || i18n.get("global.unexpectedError");
        yield put(notificationActions.showNotification(errorMessage, "error", ["changeTokenPass"]));
    } else if (response.data?.data?._result === "INVALIDO") {
        yield put(
            notificationActions.showNotification(i18n.get("settings.token.password.invalid"), "error", [
                "changeTokenPass",
            ]),
        );
    } else if (response.data?.data?._result === "DISABLED") {
        yield put(
            notificationActions.showNotification(i18n.get("settings.token.password.disabled"), "error", [
                "changeTokenPass",
            ]),
        );
    } else {
        yield put(
            notificationActions.showNotification(i18n.get("settings.token.changePassword.modify.success"), "success", [
                "tokenCreateCodeInvitation",
            ]),
        );
        yield put(push("/settings/token"));
    }
    formikBag.setSubmitting(false);
}

function* recoverTokenPass({ username, documentType, document, password, sendChannel, formikBag }) {
    const user = yield select(sessionSelectors.getUser);

    try {
        const response = yield call(
            user ? tokenMidleware.recoverTokenPass : tokenMidleware.recoverTokenPassAnonymous,
            username,
            documentType,
            document,
            password,
            sendChannel,
        );
        if (response.type === "W" || (response.data.data.error !== null && response.data.data.error !== undefined)) {
            yield put(actions.recoverTokenPasswordFailure());
            if (response.data.data.NO_FIELD) {
                yield put(
                    notificationActions.showNotification(response.data.data.NO_FIELD, "error", ["recoverTokenPass"]),
                );
            }
            if (response.data.data.error) {
                yield put(
                    notificationActions.showNotification(i18n.get(response.data.data.error), "error", [
                        "recoverTokenPass",
                    ]),
                );
            } else {
                yield put(notificationActions.showNotification(response.data.message, "error", ["recoverTokenPass"]));
            }
        } else {
            yield put(
                notificationActions.showNotification(
                    i18n.get("settings.token.recoverPassword.password.success"),
                    "success",
                    ["tokenCreateCodeInvitation", "login"],
                ),
            );
            yield put(actions.recoverTokenPasswordSuccess(response.data.data));
            yield put(push("/settings/token"));
        }
    } catch (error) {
        formikBag.setErrors(adjustIdFieldErrors(error));
    } finally {
        formikBag.setSubmitting(false);
    }
}

function* tokenPasswordValidation({ password, formikBag, userId }) {
    const idDevice = window.app.getDeviceUUID();
    const response = yield call(tokenMidleware.tokenPasswordValidationExternal, password, userId, idDevice);
    try {
        if (
            response.type === "W" ||
            (response.data.data._result !== null &&
                response.data.data._result !== undefined &&
                response.data.data._result !== "VALIDO")
        ) {
            yield put(actions.tokenPasswordValidationFailed());
            if (response.data.data._result === "INVALIDO") {
                yield put(
                    notificationActions.showNotification(i18n.get("settings.token.password.invalid"), "error", [
                        "tokenPassValidation",
                    ]),
                );
            } else if (response.data.data._result === "DISABLED") {
                yield put(
                    notificationActions.showNotification(i18n.get("settings.token.password.disabled"), "error", [
                        "tokenPassValidation",
                    ]),
                );
            } else {
                yield put(
                    notificationActions.showNotification(response.data.message, "error", ["tokenPassValidation"]),
                );
            }
        } else {
            const { _exchangeToken, showNotificationIn } = response.data.data;
            yield put({
                type: types.TOKEN_PASSWORD_VALIDATION_SUCCESS,
                exchangeToken: _exchangeToken,
                showNotificationIn,
            });

            yield put({
                type: types.TOKEN_PASSWORD_VALIDATION_SUCCESS,
                exchangeToken: _exchangeToken,
            });
            const isSettingsFlow = formikBag?.props?.match?.path?.startsWith("/settings");
            const tokenRandomCodePath = isSettingsFlow ? "/settings/token/tokenRandomCode" : "/tokenRandomCode";
            if (!_exchangeToken) {
                yield put(push("/settings/token/tokenRandomCode"));
            } else {
                // SI hay exchangeToken entonces hay token activo
                const useOfflineToken = configUtils.getBoolean("backend.show.Token.offline");
                if (useOfflineToken) {
                    // valido si hay semilla en el dispositivo
                    let seedResult;
                    try {
                        seedResult = yield call(secureStorageUtils.get, TOKEN_SEED);
                    } catch (e) {
                        // No se encontro la semilla token en el dispositivo
                    }
                    if (!seedResult) {
                        // Debo usar softtoken pero no hay semilla
                        yield put({
                            type: types.SOFT_TOKEN_MIGRATE_SEED_REQUEST,
                            idDevice,
                            password,
                            targetPath: tokenRandomCodePath,
                        });
                    } else {
                        yield put(push(tokenRandomCodePath));
                    }
                } else {
                    yield put(push(tokenRandomCodePath));
                }
            }
        }
    } catch (error) {
        yield put(notificationActions.showNotification(error.message, "error", ["tokenPassValidation"]));
    } finally {
        formikBag.setSubmitting(false);
    }
}

function* refreshExchangeToken(params) {
    const { exchangeToken, userId } = params;
    const idDevice = window.app.getDeviceUUID();
    const response = yield call(tokenMidleware.refreshTokenExternal, idDevice, userId, exchangeToken);

    if (response.data.code !== "COR000I") {
        yield put(push("/tokenPassword"));
        yield put(notificationActions.showNotification(response.data.message, "error", ["tokenPassValidation"]));
    } else {
        const { _exchangeToken, showNotificationIn } = response.data.data;
        if (_exchangeToken) {
            yield put({
                type: types.EXCHANGE_TOKEN_REFRESH_SUCCESS,
                exchangeToken: _exchangeToken,
                showNotificationIn,
            });
        } else {
            yield put(push("/tokenPassword"));
        }
    }
}

function* checkExchangeToken(params) {
    const { exchangeToken, userId } = params;
    const idDevice = window.app.getDeviceUUID();
    const response = yield call(tokenMidleware.checkTokenExternal, idDevice, userId, exchangeToken);

    if (response.data.code !== "COR000I" || !response.data.data._result) {
        yield put(push("/tokenPassword"));
        yield put(notificationActions.showNotification(response.data.message, "error", ["tokenPassValidation"]));
    }
}

function* tokenGeneration(params) {
    const { exchangeToken } = params;
    const idDevice = window.app.getDeviceUUID();
    const response = yield call(tokenMidleware.tokenGenerationExternal, idDevice, exchangeToken);
    if (response.type === "W") {
        yield put(notificationActions.showNotification("Error interno", "error", ["tokenPassValidation"]));
    } else {
        yield put(actions.tokenPasswordGenerationSuccess(response.data.data));
    }
}

function* migrateTokenSeed({ idDevice, password, isSync, targetPath }) {
    if (isPublicTokenAccess(targetPath)) {
        yield call(redirectAfterSeedMigrationFailure, targetPath);
        return;
    }

    try {
        const response = yield call(tokenMidleware.migrateTokenSeed, idDevice, password);
        if (!response || response.type === "W" || !response.data?.data?._seed) {
            yield call(handleSeedMigrationFailure, targetPath);
            return;
        }

        const result = yield call(secureStorageUtils.set, TOKEN_SEED, response.data.data._seed);

        if (result) {
            yield put(actions.softTokenMigrateSeedSuccess());
            if (!isSync) {
                yield put(push(targetPath || "/settings/token/tokenRandomCode"));
            }
        } else {
            yield call(handleSeedMigrationFailure, targetPath);
        }
    } catch (error) {
        yield call(handleSeedMigrationFailure, targetPath);
    }
}

function isPublicTokenAccess(targetPath) {
    return targetPath === "/tokenRandomCode" || targetPath === "/v3/tokenRandomCode";
}

function* handleSeedMigrationFailure(targetPath) {
    try {
        yield call(cleanupDeviceWithoutSeed);
    } catch (error) {
        // eslint-disable-next-line no-console
        console.warn("Token seed recovery cleanup failed");
    } finally {
        yield call(redirectAfterSeedMigrationFailure, targetPath);
    }
}

function* cleanupDeviceWithoutSeed() {
    const uuid = window.app.getDeviceUUID();
    const deleteResponse = yield call(tokenMidleware.deleteDeviceWithoutSeed, uuid);
    if (deleteResponse.type !== "W") {
        yield put({
            type: types.TOKEN_STATUS_PASSWORD_REVOKED_SUCCESS,
            statusToken: null,
            userIdToken: null,
        });
    }
}

function* redirectAfterSeedMigrationFailure(targetPath) {
    try {
        if (isPublicTokenAccess(targetPath)) {
            const loginPath = targetPath === "/v3/tokenRandomCode" ? "/v3/login" : "/";
            const currentPath = yield select((state) => state.router?.location?.pathname);
            const isCurrentLoginPath =
                currentPath === loginPath ||
                (loginPath === "/v3/login" && currentPath === "/v3/login/") ||
                (loginPath === "/" && currentPath === "/loginStep1");
            if (!isCurrentLoginPath) {
                yield put(push(loginPath));
            }
            yield put(
                notificationActions.showNotification(i18n.get("setting.token.seed.error.login.hint"), "warning", [
                    "login",
                ]),
            );
        } else {
            yield put(push("/settings/token/tokenSeedError"));
        }
    } finally {
        yield put(actions.softTokenMigrateSeedFailed());
    }
}

function* createTokenSeed({ seed }) {
    try {
        yield call(secureStorageUtils.set, TOKEN_SEED, seed);
    } catch (error) {
        yield put(notificationActions.showNotification("Error interno", "error", ["tokenPassValidation"]));
    }
}

function* softTokenGeneration() {
    const safewayOtpEnabled = configUtils.getBoolean("safeway.otp.enabled", false);
    if (!safewayOtpEnabled) {
        const newTokenInfo = {
            token: "111111",
            remainingTime: 30,
        };
        yield put(actions.softTokenGenerationSuccess(newTokenInfo));
        return;
    }
    const expirationTime = configUtils.getInteger("otp.token.expiration.time", 30);
    const tokenSeed = yield call(secureStorageUtils.get, TOKEN_SEED);
    if (!tokenSeed) {
        yield call(handleSeedError);
        return;
    }

    const tDiff = configUtils.getInteger("safeway.softToken.time.diference", 0);
    const result = yield call(tokenUtils.getToken, tokenSeed, expirationTime, tDiff);
    const { remainingTime, token } = result;

    const newTokenInfo = {
        token,
        remainingTime,
    };

    yield put(actions.softTokenGenerationSuccess(newTokenInfo));
}

function* getOfflineTokenValue() {
    const safewayOtpEnabled = configUtils.getBoolean("safeway.otp.enabled", false);
    const expirationTime = configUtils.getInteger("otp.token.expiration.time", 30);

    if (!safewayOtpEnabled) {
        const newTokenInfo = {
            token: "111111",
            remainingTime: expirationTime,
        };
        return newTokenInfo;
    }

    const tokenSeed = yield call(secureStorageUtils.get, TOKEN_SEED);

    const tDiff = configUtils.getInteger("safeway.softToken.time.diference", 0);
    const result = yield call(tokenUtils.getToken, tokenSeed, expirationTime, tDiff);
    const { remainingTime, token } = result;

    const newTokenInfo = {
        token,
        remainingTime,
    };

    return newTokenInfo;
}

function* tokenPasswordDisabled(params) {
    const { password, formikBag } = params;
    const response = yield call(tokenMidleware.tokenPasswordDisabled, password);

    if (
        response.type === "W" ||
        (response.data.data._result !== null &&
            response.data.data._result !== undefined &&
            response.data.data._result !== "VALIDO")
    ) {
        if (response.data.data._result === "INVALIDO") {
            yield put(
                notificationActions.showNotification(i18n.get("settings.token.password.invalid"), "error", [
                    "changeTokenPassDisabled",
                ]),
            );
        } else if (response.data.data._result === "DISABLED") {
            yield put(
                notificationActions.showNotification(i18n.get("settings.token.password.disabled"), "error", [
                    "changeTokenPassDisabled",
                ]),
            );
        } else {
            yield put(
                notificationActions.showNotification(response.data.message, "error", ["changeTokenPassDisabled"]),
            );
        }
    } else {
        if (window.cordova) {
            try {
                yield call(secureStorageUtils.remove, TOKEN_SEED);
            } catch (e) {
                // No se encontro la semilla token en el dispositivo
            }
        }
        yield put(
            notificationActions.showNotification(i18n.get("settings.token.disabledTokenPass.success"), "success", [
                "tokenCreateCodeInvitation",
            ]),
        );
        yield put(push("/settings/token"));
    }
    formikBag.setSubmitting(false);
}

function* tokenBiometricDisabled(params) {
    const { code } = params;
    const response = yield call(tokenMidleware.tokenBiometricDisabled, code);

    if (
        response.type === "W" ||
        (response.data.data._result !== null &&
            response.data.data._result !== undefined &&
            response.data.data._result !== "VALIDO")
    ) {
        yield put({
            type: types.TOKEN_BIOMETRIC_DISABLED_FAILED,
        });
        yield put(notificationActions.showNotification(response.data.data._result, "error", ["tokenBiometricDisable"]));
    } else {
        if (window.cordova) {
            try {
                yield call(secureStorageUtils.remove, TOKEN_SEED);
            } catch (e) {
                // No se encontro la semilla token en el dispositivo
            }
        }
        yield put({
            type: types.TOKEN_BIOMETRIC_DISABLED_SUCCESS,
        });
        yield put(
            notificationActions.showNotification(i18n.get("settings.token.disabledTokenPass.success"), "success", [
                "tokenCreateCodeInvitation",
            ]),
        );
        yield put(push("/settings/token"));
    }
}

function* tokenBiometricDisablePre(params) {
    const { channel } = params;
    const response = yield call(tokenMidleware.tokenPasswordDisabledPre, channel);

    if (response.type === "W") {
        yield put({
            type: types.TOKEN_BIOMETRIC_DISABLED_PRE_SUCCESS,
        });
        yield put(
            notificationActions.showNotification(i18n.get("settings.token.password.invalid"), "error", [
                "tokenBiometricDisable",
            ]),
        );
    } else {
        yield put({
            type: types.TOKEN_BIOMETRIC_DISABLED_PRE_FAILED,
        });
        yield put(
            notificationActions.showNotification(
                i18n.get("settings.token.disabledTokenPass.disable.success"),
                "success",
                ["tokenBiometricDisable"],
            ),
        );
    }
}

// Se utiliza solo en mobile, guarda el estado y userId en la memoria de la aplicacion
function* getTokenStatusMobile(params) {
    const { userId } = params;

    const idDevice = window.app.getDeviceUUID();

    const response = yield call(tokenMidleware.getTokenStatus, idDevice);

    if (response.type === "W") {
        yield put(actions.tokenStatusPasswordFailed());
    } else {
        const { statusToken } = response.data.data;
        yield put({
            type: types.TOKEN_STATUS_PASSWORD_SUCCESS,
            statusToken,
            userIdToken: userId,
        });
        yield call(recoverMissingTokenSeed, statusToken, idDevice);
    }
}

function* recoverMissingTokenSeed(statusToken, idDevice) {
    const useOfflineToken = configUtils.getBoolean("backend.show.Token.offline");
    const safewayOtpEnabled = configUtils.getBoolean("safeway.otp.enabled", false);
    if (statusToken !== "active" || !useOfflineToken || !safewayOtpEnabled) {
        return;
    }

    let tokenSeed;
    try {
        tokenSeed = yield call(secureStorageUtils.get, TOKEN_SEED);
    } catch (error) {
        tokenSeed = null;
    }

    if (!tokenSeed) {
        yield put({
            type: types.SOFT_TOKEN_MIGRATE_SEED_REQUEST,
            idDevice,
            isSync: true,
        });
    }
}

function* getTokenStatusMobileAnonymous(params) {
    try {
        const { userId } = params;

        const idDevice = window.app.getDeviceUUID();

        const response = yield call(tokenMidleware.getTokenStatusAnonymous, idDevice, userId);
        if (response.type === "W") {
            yield put(actions.tokenStatusPasswordFailed());
        } else {
            yield put({
                type: types.TOKEN_STATUS_PASSWORD_SUCCESS,
                statusToken: response.data.data.statusToken,
                userIdToken: userId,
            });
        }
    } catch (error) {
        yield put(actions.tokenStatusPasswordFailed());
    }
}

function* changeNewToken() {
    const response = yield call(tokenMidleware.changeNewToken);
    if (response.type === "W") {
        yield put(actions.tokenCreatePreviewFailed());

        if (response.data.data.NO_FIELD) {
            yield put(
                notificationActions.showNotification(response.data.data.NO_FIELD, "error", [
                    "tokenCreateCodeInvitation",
                ]),
            );
        } else {
            yield put(
                notificationActions.showNotification(i18n.get("global.unexpectedError"), "error", [
                    "tokenCreateCodeInvitation",
                ]),
            );
        }
    } else {
        yield put({ type: types.TOKEN_SHOW_NEW_MODAL });
        // Refrescar web/native: sale de "token en otro dispositivo" y el CTA usa TokenDrawer (SMS/email).
        yield call(checkUserActiveTokenWeb);
        if (window.isMobileApp()) {
            yield call(getTokenStatusMobile, { userId: getLoggedUserId() });
        }

        const uxDashboardOn = configUtils.getBoolean("frontend.UXImprovements.Dashboard.functionalities", false);
        if (uxDashboardOn) {
            yield put(push("/settings/token/request"));
            yield put(actions.setNewModal(false));
        } else {
            // Misma envoltura que "Solicitar token": TokenDrawer dentro de ButtonDrawer en /settings/token
            yield put(push("/settings/token"));
        }
    }
}

function* tokenOTPValidation({ codeRandom, formikBag }) {
    const response = yield call(tokenMidleware.tokenSyncNewDevicePreview, codeRandom);

    if (response.type === "W" || response.data.data.message !== null) {
        yield put(actions.tokenSyncNewDevicePreviewFailed());

        yield put(
            notificationActions.showNotification(i18n.get(response.data.data.message), "error", [
                "tokenValidateRandomCode",
            ]),
        );
    } else {
        yield put(actions.tokenSyncNewDevicePreviewSuccess(response.data.data));
        const useBiometricToken = configUtils.getBoolean("backend.show.Biometric.Token.offline");
        if (useBiometricToken) {
            yield put(push("/settings/token/synchronization/biometricActivation"));
        } else {
            yield put(push("/settings/token/synchronization/tokenPassword"));
        }
    }
    formikBag.setSubmitting(false);
}

function* recoverTokenAnonymous({ values, formikBag, config }) {
    const response = yield call(tokenMidleware.recoverTokenAnonymous, values);

    if (response.type === "W" || response.data.data.error) {
        yield put(actions.tokenRecoverAnonymousFailed());

        yield put(
            notificationActions.showNotification(
                i18n.get(response.type === "W" ? response.message : response.data.data.error),
                "error",
                ["recoverTokenPass"],
            ),
        );
    } else {
        yield put(actions.tokenRecoverAnonymousSuccess(response.data.data));

        if (config) {
            const resp = yield select(selectors.getTokenAnonymousData);

            yield put(actions.configureSelectChannel({ ...config, ...resp }));

            yield put(push("/settings/token/selectChannel"));
        }
    }
    formikBag.setSubmitting(false);
}

function* tokenSyncNewDevice({ password, formikBag }) {
    let idDevice = null;

    if (window.app) {
        idDevice = window.app.getDeviceUUID();
    } else {
        idDevice = yield deviceUtils.getDeviceFingerprint();
    }

    const response = yield call(tokenMidleware.tokenSyncNewDevice, password, idDevice);
    if (
        response.type === "W" ||
        (response.data.data._result !== null &&
            response.data.data._result !== undefined &&
            response.data.data._result !== "VALIDO")
    ) {
        yield put(actions.tokenPasswordValidationFailed());
        if (response.data.data._result === "INVALIDO") {
            yield put(
                notificationActions.showNotification(i18n.get("settings.token.password.invalid"), "error", [
                    "tokenPassValidation",
                ]),
            );
        } else if (response.data.data._result === "DISABLED") {
            yield put(
                notificationActions.showNotification(i18n.get("settings.token.password.disabled"), "error", [
                    "tokenPassValidation",
                ]),
            );
        } else {
            yield put(notificationActions.showNotification(response.data.message, "error", ["tokenPassValidation"]));
        }
    } else {
        if (window.isMobileApp()) {
            const userId = getLoggedUserId();
            yield put({
                type: types.TOKEN_STATUS_PASSWORD_REQUEST,
                userId,
            });
            const useOfflineToken = configUtils.getBoolean("backend.show.Token.offline");
            if (useOfflineToken) {
                let seedResult;
                try {
                    seedResult = yield call(secureStorageUtils.get, TOKEN_SEED);
                } catch (e) {
                    // No se encontro la semilla token en el dispositivo
                }
                if (!seedResult) {
                    const isSync = true;
                    yield put({
                        type: types.SOFT_TOKEN_MIGRATE_SEED_REQUEST,
                        idDevice,
                        password,
                        isSync,
                    });
                }
            }
        }
        yield put(
            notificationActions.showNotification(i18n.get("settings.token.validate.codeRandom.success"), "success", [
                "tokenCreateCodeInvitation",
            ]),
        );
        yield put(push("/settings/token"));
    }
    if (formikBag) {
        formikBag.setSubmitting(false);
    }
}

function* deleteDeviceWithoutSeed() {
    const tokenStatus = yield select(selectors.getStatusTokenPass);
    if (tokenStatus != null && !isTokenActive()) {
        const uuid = window.app.getDeviceUUID();
        const response = yield call(tokenMidleware.deleteDeviceWithoutSeed, uuid);
        if (response.type !== "W") {
            yield put({
                type: types.TOKEN_STATUS_PASSWORD_REVOKED_SUCCESS,
                statusToken: null,
                userIdToken: null,
            });
        }
    }
}

function* deleteSeedFromDevice() {
    const useOfflineToken = configUtils.getBoolean("backend.show.Token.offline");
    if (!isTokenActive() || !useOfflineToken) {
        try {
            yield call(secureStorageUtils.remove, TOKEN_SEED);
        } catch (e) {
            // No se encontro la semilla token en el dispositivo o no fue posible eliminarla
        }
    }
}

function* verifyTokenSeed() {
    let tokenSeedAvailable = false;

    try {
        const tokenSeed = yield call(secureStorageUtils.get, TOKEN_SEED);
        tokenSeedAvailable = !!tokenSeed;
    } catch (error) {
        tokenSeedAvailable = false;
    }

    yield put({
        type: types.TOKEN_SEED_VERIFY_SUCCESS,
        tokenSeedAvailable,
    });
}

// Valida biometria y redirige a token
function* validateBiometrics({ targetPath } = {}) {
    try {
        const availability = yield call(fingerprintUtils.isAvailable);
        const hardwareAvailability = availability && availability.isAvailable && availability.isHardwareDetected;
        if (hardwareAvailability) {
            const disableBackup = false;
            const result = yield call(fingerprintUtils.verifyAio, disableBackup);
            if (result && result.withFingerprint) {
                let seedResult;
                try {
                    seedResult = yield call(secureStorageUtils.get, TOKEN_SEED);
                } catch (e) {
                    // No se encontro la semilla token en el dispositivo
                }
                const hasActiveSession = yield select(sessionSelectors.isLoggedIn);
                const isDashboardV3Enabled = configUtils.getBoolean(
                    "frontend.UXImprovements.Dashboard.functionalities",
                    false,
                );
                let defaultTargetPath;
                if (hasActiveSession) {
                    defaultTargetPath = "/settings/token/tokenRandomCode";
                } else if (isDashboardV3Enabled) {
                    defaultTargetPath = "/v3/tokenRandomCode";
                } else {
                    defaultTargetPath = "/tokenRandomCode";
                }
                const resolvedTargetPath = targetPath || defaultTargetPath;
                if (!seedResult) {
                    const idDevice = window.app.getDeviceUUID();
                    yield put({
                        type: types.SOFT_TOKEN_MIGRATE_SEED_REQUEST,
                        idDevice,
                        targetPath: resolvedTargetPath,
                    });
                } else {
                    yield put(push(resolvedTargetPath));
                }
            }
        } else {
            yield call(fingerprintUtils.enrollOnDevice);
        }
    } catch (error) {
        const errorCode = error.code;
        // eslint-disable-next-line no-empty
        if (
            errorCode === fingerprintUtils.fingerprintErrors.BIOMETRIC_LOCKED_OUT ||
            errorCode === fingerprintUtils.fingerprintErrors.BIOMETRIC_LOCKED_OUT_PERMANENT ||
            errorCode === fingerprintUtils.fingerprintErrors.BIOMETRIC_NOT_ENROLLED
        ) {
            const message = i18n.get("settings.fingerprintConfiguration.dialog.error_1");
            const message2 = i18n.get("settings.fingerprintConfiguration.dialog.error_2");
            yield put(
                notificationActions.showNotification(`${message} ${message2}`, "error", [
                    "tokenCreateCodeInvitation",
                    "login",
                    "desktop",
                ]),
            );
        } else if (
            errorCode !== fingerprintUtils.fingerprintErrors.BIOMETRIC_DISMISSED &&
            errorCode !== fingerprintUtils.fingerprintErrors.BIOMETRIC_PIN_OR_PATTERN_DISMISSED
        ) {
            yield put(
                notificationActions.showNotification(error.message, "error", [
                    "tokenCreateCodeInvitation",
                    "login",
                    "desktop",
                ]),
            );
        }
    }
}

function* tokenBiometricVerify() {
    try {
        const availability = yield call(fingerprintUtils.isAvailable);
        const hardwareAvailability = availability && availability.isAvailable && availability.isHardwareDetected;
        if (hardwareAvailability) {
            const disableBackup = false;
            const result = yield call(fingerprintUtils.verifyAio, disableBackup);
            if (result && result.withFingerprint) {
                yield put(actions.setTokenBiometricValid(true));
            } else {
                yield put(actions.setTokenBiometricValid(false));
            }
        } else {
            yield call(fingerprintUtils.enrollOnDevice);
        }
    } catch (error) {
        const errorCode = error.code;
        // eslint-disable-next-line no-empty
        if (
            errorCode === fingerprintUtils.fingerprintErrors.BIOMETRIC_LOCKED_OUT ||
            errorCode === fingerprintUtils.fingerprintErrors.BIOMETRIC_LOCKED_OUT_PERMANENT ||
            errorCode === fingerprintUtils.fingerprintErrors.BIOMETRIC_NOT_ENROLLED
        ) {
            const message = i18n.get("settings.fingerprintConfiguration.dialog.error_1");
            const message2 = i18n.get("settings.fingerprintConfiguration.dialog.error_2");
            yield put(
                notificationActions.showNotification(`${message} ${message2}`, "error", [
                    "activateTokenBiometric",
                    "login",
                    "desktop",
                ]),
            );
        } else if (
            errorCode !== fingerprintUtils.fingerprintErrors.BIOMETRIC_DISMISSED &&
            errorCode !== fingerprintUtils.fingerprintErrors.BIOMETRIC_PIN_OR_PATTERN_DISMISSED
        ) {
            yield put(
                notificationActions.showNotification(error.message, "error", [
                    "activateTokenBiometric",
                    "login",
                    "desktop",
                ]),
            );
        }
    }
}

function* generateBiometricsToken(params) {
    const { submitForm, form, field } = params;
    const biometricNotificationScopes = ["recoveryPassword", "recoveryUserpass"];
    try {
        const availability = yield call(fingerprintUtils.isAvailable);
        const hardwareAvailability = availability && availability.isAvailable && availability.isHardwareDetected;
        if (hardwareAvailability) {
            const disableBackup = false;
            const result = yield call(fingerprintUtils.verifyAio, disableBackup);
            if (result && result.withFingerprint) {
                const response = yield call(getOfflineTokenValue);
                if (!response) {
                    yield put(
                        notificationActions.showNotification("Error interno", "error", biometricNotificationScopes),
                    );
                } else if (form && field) {
                    const { token } = response;
                    form.setValues({ ...form.values, [field.name]: token });
                    submitForm();
                }
            }
        } else {
            yield call(fingerprintUtils.enrollOnDevice);
        }
    } catch (error) {
        const errorCode = error?.code;
        if (
            errorCode === fingerprintUtils.fingerprintErrors.BIOMETRIC_LOCKED_OUT ||
            errorCode === fingerprintUtils.fingerprintErrors.BIOMETRIC_LOCKED_OUT_PERMANENT ||
            errorCode === fingerprintUtils.fingerprintErrors.BIOMETRIC_NOT_ENROLLED
        ) {
            const message = configUtils.get("biometric.NumberAttemptsPassed.Snackbar");
            yield put(notificationActions.showNotification(message, "error", biometricNotificationScopes));
        } else if (error?.response && error.response?.status && error.response.status === 401) {
            yield put(
                notificationActions.showNotification(
                    i18n.get("login.fingerprint.session.expired"),
                    "error",
                    biometricNotificationScopes,
                ),
            );
        } else if (
            errorCode !== fingerprintUtils.fingerprintErrors.BIOMETRIC_DISMISSED &&
            errorCode !== fingerprintUtils.fingerprintErrors.BIOMETRIC_PIN_OR_PATTERN_DISMISSED
        ) {
            const message = deviceUtils.getDevicePlatform() === "iOS" ? error?.localizedDescription : error?.message;
            if (message) {
                yield put(notificationActions.showNotification(message, "error", biometricNotificationScopes));
            }
        }
    }
    yield put({ type: types.TOKEN_BIOMETRICS_GENERATOR_FINISHED });
}

function* handleSeedError() {
    const recoveringSeedError = yield select(selectors.getRecoveringSeedError);
    if (recoveringSeedError) {
        return;
    }

    yield put(actions.tokenSeedErrorRecoveryRequest());
    try {
        yield call(cleanupDeviceWithoutSeed);
    } finally {
        yield put(push("/settings/token/tokenSeedError"));
        yield put(actions.tokenSeedErrorRecoveryFinished());
    }
}

function* sessionExpired(loggedUser) {
    if (!loggedUser) {
        yield put(push("/"));
        yield put(notificationActions.showNotification(i18n.get("session.expired"), "error", ["login"]));
    } else {
        yield put(push("/desktop"));
    }
}
